// src/components/RequireOnboarding.tsx
import React from "react";
import { useNavigate } from "react-router-dom";
import { signOut } from "firebase/auth";

import { auth } from "../firebase/config";
import { useAuth } from "../context/AuthContext";
import styles from "./FtuWelcome.module.css";

interface RequireOnboardingProps {
  children: React.ReactNode;
}

const RequireOnboarding: React.FC<RequireOnboardingProps> = ({ children }) => {
  const { user, loading, ftuCompleted } = useAuth();
  const navigate = useNavigate();

  const handleStart = () => navigate("/ftu");

  const handleLater = async () => {
    const confirmed = window.confirm(
      "You will be logged out and can finish these questions next time you log in. Continue?"
    );
    if (!confirmed) return;
    try {
      await signOut(auth);
    } catch (err) {
      console.error("Error signing out from RequireOnboarding:", err);
    }
    navigate("/login", { replace: true });
  };

  return (
    <>
      {/* Always render children immediately — never block with null. 
          This prevents Dashboard from unmounting/remounting while auth resolves.
      */}
      {children}

      {/* ── HIGH QUALITY BRANDED SPINNER OVERLAY ── */}
      {loading && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: '100vw',
          height: '100%',
          minHeight: '-webkit-fill-available', 
          backgroundColor: '#fcf9f7',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2147483647,
          pointerEvents: 'all'
        }}>
          {/* Spinner */}
          <div style={{
            width: '50px',
            height: '50px',
            border: '4px solid rgba(30, 94, 91, 0.1)',
            borderTop: '4px solid #1e5e5b',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
            marginBottom: '20px',
            boxSizing: 'border-box'
          }} />

          {/* Branding */}
          <div style={{
            fontFamily: 'DM Sans, sans-serif',
            textAlign: 'center'
          }}>
            <p style={{
              color: '#0c2624',
              fontSize: '18px',
              fontWeight: '600',
              letterSpacing: '2px',
              textTransform: 'uppercase',
              margin: 0,
              animation: 'pulse 1.5s ease-in-out infinite'
            }}>
              BodyOwn
            </p>
          </div>

          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
            @keyframes pulse {
              0%, 100% { opacity: 1; }
              50% { opacity: 0.5; }
            }
            body { overflow: hidden !important; }
          `}</style>
        </div>
      )}

      {/* ── ONBOARDING OVERLAY ── */}
      {!loading && ftuCompleted === false && user && (
        <div className={styles.overlay}>
          <div className={styles.popup}>
            <div className={styles.iconRow}>
              <span className={styles.icon} aria-hidden="true">
                📋
              </span>
              <h1 className={styles.title}>Let's tailor your recovery</h1>
            </div>

            <p className={styles.body}>
              We'll ask a few quick questions so BodyOwn can understand how
              you're feeling and tailor guidance to your recovery.
            </p>
            <p className={styles.bodySecondary}>
              It only takes a couple of minutes.
            </p>

            <div className={styles.buttonContainer}>
              <button
                type="button"
                onClick={handleStart}
                className={`${styles.button} ${styles.startButton}`}
              >
                Start my recovery check
              </button>

              <button
                type="button"
                onClick={handleLater}
                className={`${styles.button} ${styles.laterButton}`}
              >
                Skip for now
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default RequireOnboarding;